import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'

import {Box, HStack, Button, Heading} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Link} from 'react-router-dom'

// Others
import {categoryUrlBuilder} from '@salesforce/retail-react-app/app/utils/url'
import useMultiSite from '../../../hooks/use-multi-site'
import {useGTM} from '@app/hooks/use-gtm'

/**
 * LinkedCategories is a React functional component that renders a horizontal list of category links.
 *
 * @param {Object} props - The properties object.
 * @param {Array} props.categories - An array of category objects to be displayed. Each category object should contain a `displayName` and other relevant properties.
 *
 * @returns {JSX.Element} A JSX element that represents a navigational list of linked categories. If no categories are provided, an empty Box component is returned.
 *
 * The component utilizes the `useIntl` hook to access internationalization features and the `useMultiSite` hook to build URLs for different categories.
 *
 * The categories are displayed as a horizontal, scrollable list using a combination of Box and HStack components. Each category is rendered as a Button component styled as a link, which navigates to the category's URL.
 *
 * The component is styled to be responsive, with specific spacing and margin adjustments for different screen sizes.
 */
const LinkedCategories = ({categories = [], containerProps = {}}) => {
    const intl = useIntl()
    const {buildUrl} = useMultiSite()
    const {triggerCatalogTabClickEvent} = useGTM()
    if (categories.length === 0) {
        return null
    }

    const triggerCatalogTabClick = (linkTo, categoryName) => {
        triggerCatalogTabClickEvent(linkTo, categoryName)
    }
    return (
        <Box
            overflowX="auto"
            whiteSpace="nowrap"
            pb={9}
            m={[10, null, '13px 20px 4px']}
            {...containerProps}
            as="nav"
            aria-label={intl.formatMessage({
                id: 'plp.product_categories',
                defaultMessage: 'Product categories'
            })}
        >
            <HStack spacing={[0, null, 6]} as="ul" listStyleType={'none'}>
                {categories.map((category) => {
                    const linkTo = buildUrl(categoryUrlBuilder(category, intl.locale))
                    return (
                        <Box key={category.id} as="li" p={'3px 0'}>
                            <Heading as="h2">
                                <Button
                                    as={Link}
                                    to={linkTo}
                                    onClick={() => {
                                        triggerCatalogTabClick(linkTo, category.displayName)
                                    }}
                                    variant="secondary"
                                    size="sm"
                                    flexShrink={0}
                                    mr={[-1, null, 0]}
                                    color="darkGray"
                                    lineHeight={1}
                                >
                                    {category.displayName}
                                </Button>
                            </Heading>
                        </Box>
                    )
                })}
            </HStack>
        </Box>
    )
}

LinkedCategories.propTypes = {
    categories: PropTypes.array,
    containerProps: PropTypes.object
}

export default LinkedCategories
